import React from "react";
import { useBranding } from "../lib/branding";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ICON_URL = "/icons/icon-192.png";

/**
 * Renders the app logo. The logo is responsive: on phones it shrinks
 * automatically so it never overlaps the nav buttons / menu trigger.
 *
 * Layout safety:
 *  - `min-w-0` + `shrink` lets the brand block compress in a flex row.
 *  - `max-w-[...]` keeps user-uploaded custom logos from bleeding into
 *    the buttons on the right side of the nav.
 *  - Heights use Tailwind responsive classes (h-7 on phones, h-9 on
 *    tablet+) instead of inline pixel values so they adapt cleanly.
 */
export const BrandLogo = () => {
    const { logoUrl, appName } = useBranding();
    const customUrl = logoUrl
        ? logoUrl.startsWith("http")
            ? logoUrl
            : `${API_BASE}${logoUrl}`
        : "";

    if (customUrl) {
        return (
            <div
                className="flex items-center gap-2 select-none min-w-0 shrink"
                data-testid="brand-logo"
            >
                <img
                    src={customUrl}
                    alt={appName || "Logo"}
                    // iter-130: render custom logos as an off-white,
                    // see-through mark on the DARK nav bar via
                    // invert+screen blending. In LIGHT mode we drop
                    // both — the artwork is already dark-on-white, so
                    // it reads naturally against the white top bar.
                    className="brand-custom-logo h-7 sm:h-9 w-auto max-w-[120px] sm:max-w-[180px] object-contain"
                    draggable="false"
                    data-testid="brand-logo-custom"
                />
            </div>
        );
    }

    return (
        <div
            className="flex items-center gap-1.5 sm:gap-2 select-none min-w-0 shrink"
            data-testid="brand-logo"
        >
            <img
                src={DEFAULT_ICON_URL}
                alt={appName || "Steeltooth"}
                className="brand-custom-logo h-7 sm:h-9 w-auto object-contain shrink-0"
                draggable="false"
                data-testid="brand-logo-icon"
            />
            <span
                className="text-white text-sm sm:text-base font-bold tracking-tight truncate"
                data-testid="brand-logo-wordmark"
            >
                {appName || "Steeltooth"}
            </span>
        </div>
    );
};

export default BrandLogo;
